import _                              from "underscore";
import React, { ReactNode, useState } from 'react';
import { Menu, SemanticICONS }        from "semantic-ui-react";

import { MenuizedFocusContainerLayout, MenuizedFocusContainerLayoutProps } from "@src/views/containers/MenuizedFocusContainerLayout";
import { MenuItemWithIconAndIndicator }                                    from "@src/views/components/menus/MenuItemWithIconAndIndicator";
import { runIfFunction }                                                   from "@src/util";


export type MenuizedFocusContainerViewTypeConfig<T> = {
    menuLabel: string,
    menuIcon: SemanticICONS,
    onMenuClick?: () => void,
    minOptionWidth?: string,
    maxOptionWidth?: string,
    viewMetaData?: () => any,
    viewOptionsData?: () => any,
    renderContent: ( context: MenuizedFocusedViewValue<T> ) => ReactNode;
    renderOptions: ( context: MenuizedFocusedViewValue<T> ) => ReactNode;
    exclude?: boolean,
}

export type MenuizedFocusedViewValue<T = string> = {
    viewType: T,
    viewMetaData: any,
    viewOptionsData: any,
}

interface MenuizedFocusContainerProps<T extends string | number | symbol = string> {
    viewsTypes: Record<T, MenuizedFocusContainerViewTypeConfig<T>>
    initialViewType?: T,
    layoutProps?: Pick<MenuizedFocusContainerLayoutProps<T>, 'minMenuWidth' | 'maxMenuWidth' | 'minContainerHeight'>,
}

const MenuizedFocusContainerDefaultProps = {
    initialViewType: 'default',
} satisfies Partial<MenuizedFocusContainerProps>

function MenuizedFocusContainer<T extends string | number | symbol = string>( _props: MenuizedFocusContainerProps<T> ){

    const props = { ...MenuizedFocusContainerDefaultProps, ..._props };

    const getViewTypeState = ( viewType: T ) => {

        const typeData                          = props.viewsTypes[ viewType ],
              { viewMetaData, viewOptionsData } = typeData;

        return {
            viewType:        viewType,
            viewMetaData:    _.isFunction( viewOptionsData ) ? viewOptionsData() : {},
            viewOptionsData: _.isFunction( viewMetaData ) ? viewMetaData() : {},
        }
    }

    const [ focusedView, setFocusedView ] = useState<MenuizedFocusedViewValue<T>>( getViewTypeState( props.initialViewType as T ) );

    const handleMenuClick = ( viewType: any ) => {

        runIfFunction( props.viewsTypes[ viewType ].onMenuClick );

        setFocusedView(
            getViewTypeState( viewType )
        );

    }

    const currentViewType   = props.viewsTypes[ focusedView.viewType ],
          hasOptionsContent = _.isFunction( currentViewType.renderOptions );

    return (
        <MenuizedFocusContainerLayout
            { ...props.layoutProps }
            minOptionWidth={ props.viewsTypes[ focusedView.viewType ].minOptionWidth ?? null }
            maxOptionWidth={ props.viewsTypes[ focusedView.viewType ].maxOptionWidth ?? null }
            menuContent={ (
                <>
                    {/* View Toggle Menu */ }
                    <Menu fluid vertical size={ 'small' }>

                        {
                            _.chain( props.viewsTypes )
                             .pairs()
                             .map( ( [ type, _config ] ) => {

                                 const config = _config as MenuizedFocusContainerViewTypeConfig<T>;

                                 if( config.exclude ) return null;

                                 return (
                                     <MenuItemWithIconAndIndicator
                                         withIndicator={ false }
                                         onClick={ () => handleMenuClick( type ) }
                                         menuIcon={ config.menuIcon }
                                         isActive={ focusedView.viewType === type }>{ config.menuLabel }</MenuItemWithIconAndIndicator>

                                 )
                             } )
                             .compact()
                             .value()
                        }

                    </Menu>
                </>
            ) }
            viewContent={ (
                currentViewType.renderContent( focusedView )
            ) }
            optionsContent={ hasOptionsContent && (
                currentViewType.renderOptions( focusedView )
            ) }
        />
    );
}

export {
    MenuizedFocusContainer,
    MenuizedFocusContainerDefaultProps,
    type MenuizedFocusContainerProps,
};
